import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { BookingListItemDto, IBookAppointmentObject } from 'models/bookappointment';
import { IStore } from 'redux/IStore';
import { connect } from 'react-redux';
import { reduxForm, getFormSyncErrors } from 'redux-form';
import { RouteComponentProps } from 'react-router';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { Button, ErrorSummary, Spinner } from 'components';
import { detectIncompatibleBrowsers, formatCurrency, formatDuration } from 'helpers/common';
import { fullServiceName } from '../helpers';
import { IOfficeMember } from 'models/shop/members';
import { IOffice } from 'models/shop/office';
import renderIcon from 'helpers/renderIcon';
import { PortalPageSection } from 'components/PortalStructure';
import { momentWithTimezone } from 'redux/modules/shop/helpers';

interface IParams {
	shopId: string;
	officeId: string;
}

interface IProps
	extends ReturnType<typeof stateToProps>,
		ITranslateProps,
		InjectedFormProps<IBookAppointmentObject>,
		RouteComponentProps<IParams, {}> {
	route: any;
	servicesParentIds: string[];
	mobileShopOrderDetailsElement: any;
	onSubmit?: (form: IBookAppointmentObject) => void;
	previousPage?: () => void;
}

interface IState {
	filtersDrawer: boolean;
	selectedPayment: 'payNow' | 'payLater';
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class BookAppointmentStepReviewForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.state = {
			filtersDrawer: false,
			selectedPayment: null,
		};
	}

	public componentDidMount() {
		this.setState({ selectedPayment: this.props.shopCard.allowUnpaidOrders ? 'payLater' : 'payNow' }); // Set to first available option on UI

		this.checkIfSelected();

		if (detectIncompatibleBrowsers()) {
			document.body.scrollTo({ top: 0, left: 0 });
		} else {
			window.scrollTo({ top: 0, left: 0 });
		}
	}

	public componentDidUpdate(): void {
		this.checkIfSelected();
	}

	public onSubmit(form: IBookAppointmentObject) {
		this.props.onSubmit(JSON.parse(JSON.stringify({ ...form, selectedPayment: this.state.selectedPayment })));
	}

	private checkIfSelected() {
		const { currentFormValues, previousPage } = this.props;
		if (!currentFormValues.selectedAppointment && previousPage) {
			previousPage();
		}
	}

	private get staffById() {
		const { shopCard } = this.props;
		const staffById: { [userId: string]: IOffice['staff'][number] } = {};
		for (const office of shopCard.offices || []) {
			for (const staff of (office.staff || []) as IOfficeMember[]) {
				staffById[staff.id] = staff;
			}
		}
		return staffById;
	}

	private renderService(appointment: BookingListItemDto) {
		const { t, shopBookingTemplates, serviceGroups, shopCard } = this.props;
		const currencyKey = shopCard.currencyKey;
		const staffById = this.staffById;
		const appointmentAny: any = appointment;
		const office = shopCard.offices.find((office) => office.id === appointment.officeId);
		{
			/*checked*/
		}
		const start = momentWithTimezone(office.zoneInfo, appointment.schedule);
		let duration: JSX.Element = null;
		let end = null;

		if (appointment?.availableBookings.length && appointment.schedule) {
			end = start.clone().add(appointmentAny.duration, 'seconds');
			duration = <>{formatDuration(appointmentAny.duration)}</>;
		}

		const parts: JSX.Element[] = [];

		appointment.availableBookings?.map((ab) => {
			const service = (shopBookingTemplates.services || []).find((s) => s.id === ab.bookingTemplateId);
			const serviceCustomData = JSON.parse(service.customData || '{}');
			// bundles have the sum of their services in customData
			const originalPrice = serviceCustomData.priceSum || ab.originalPrice;
			let details: JSX.Element = null;
			let resourceEl: JSX.Element = null;

			if (ab.resourceId && staffById[ab.resourceId]) {
				const fullName = staffById[ab.resourceId].name.split(' ');
				resourceEl = (
					<span>
						{fullName[0]} {fullName[fullName.length - 1].charAt(0)}.
					</span>
				);
			}

			let name = <div>{appointment.name}</div>;

			if (service) {
				const fullServiceNameVar = fullServiceName(service, serviceGroups, t); // index 0 is service category, 1 is service group, 2 is service name

				// If subservice
				if (fullServiceNameVar?.length === 4) {
					name = (
						<p>
							{fullServiceNameVar[1]} <span className="serviceFullNameSlash">/</span> {fullServiceNameVar[2]}{' '}
							<span className="serviceFullNameSlash">/</span> {fullServiceNameVar[fullServiceNameVar.length - 1]}
						</p>
					);
				} else {
					// else service
					name = (
						<p>
							{fullServiceNameVar[1]} <span className="serviceFullNameSlash">/</span>{' '}
							{fullServiceNameVar[fullServiceNameVar.length - 1]}
						</p>
					);
				}

				details = (
					<span className="bookingAppointment__reviewStep__appointment__serviceSubdetails__duration">
						{formatDuration(service.duration)}
					</span>
				);

				const price: JSX.Element =
					ab.price === originalPrice ? (
						<div className="bookingAppointment__reviewStep__appointment__serviceSubdetails__price">
							{formatCurrency(currencyKey, service.price)}
						</div>
					) : (
						<div className="bookingAppointment__reviewStep__appointment__serviceSubdetails__price">
							<span className="bookingAppointment__reviewStep__appointment__serviceSubdetails__originalPriceSpan">
								{formatCurrency(currencyKey, originalPrice)}
							</span>
							<span className="bookingAppointment__reviewStep__appointment__serviceSubdetails__priceSpan">
								{formatCurrency(currencyKey, service.price)}
							</span>
						</div>
					);

				const bundleElements: JSX.Element[] = [];

				// If bundle
				if (ab?.childrenBookings?.length > 0) {
					ab?.childrenBookings.forEach((cb: any) => {
						const childService = service?.childrenBookingTemplates.find((s) => s.id === cb.bookingTemplateId);

						if (childService) {
							const fullChildrenServiceNameVar = fullServiceName(childService, serviceGroups, t); // index 0 is service category, 1 is service group, 2 is service name
							let childName: JSX.Element = null;

							// If subservice
							if (fullChildrenServiceNameVar?.length === 4) {
								childName = (
									<>
										{fullChildrenServiceNameVar[1]} <span className="serviceFullNameSlash">/</span>{' '}
										{fullChildrenServiceNameVar[2]} <span className="serviceFullNameSlash">/</span>{' '}
										{fullChildrenServiceNameVar[fullChildrenServiceNameVar.length - 1]}
									</>
								);
							} else {
								// else service
								childName = (
									<>
										{fullChildrenServiceNameVar[1]} <span className="serviceFullNameSlash">/</span>{' '}
										{fullChildrenServiceNameVar[fullChildrenServiceNameVar.length - 1]}
									</>
								);
							}

							let childResourceEl: JSX.Element = null;

							if (cb?.resourceId && staffById[cb.resourceId]) {
								const childFullName = staffById[cb.resourceId].name.split(' ');
								childResourceEl = (
									<span>
										{childFullName[0]} {childFullName[childFullName.length - 1].charAt(0)}.
									</span>
								);
							}

							bundleElements.push(
								<div className="col-12" key={cb.id}>
									<div className="bookingAppointment__reviewStep__appointment__bundleServicesFullName">{childName}</div>
									<div className="bookingAppointment__reviewStep__appointment__bundleSubdetails">{childResourceEl}</div>
								</div>,
							);
						}
					});

					parts.push(
						<div className="bookingAppointment__reviewStep__appointment__bundleWrapper" key={ab.id}>
							<div className="row">
								<div className="col-12">
									<div className="bookingAppointment__reviewStep__appointment__bundleFullName">
										{fullServiceNameVar[2]}
									</div>
								</div>

								{bundleElements}

								<div className="col-12">
									<div className="bookingAppointment__reviewStep__appointment__bundleSubdetails">
										{details}
										{price}
									</div>
								</div>
							</div>
						</div>,
					);
				} else {
					// else service
					parts.push(
						<div className="bookingAppointment__reviewStep__appointment__serviceWrapper" key={ab.id}>
							<div className="row">
								<div className="col-12">
									<div className="bookingAppointment__reviewStep__appointment__serviceFullName">{name}</div>
								</div>
								<div className="col-12">
									<div className="bookingAppointment__reviewStep__appointment__serviceSubdetails">
										{resourceEl}
										{details}
										{price}
									</div>
								</div>
							</div>
						</div>,
					);
				}
			}
		});

		return (
			<div className="bookingAppointment__reviewStep__appointment">
				<div className="bookingAppointment__reviewStep__appointment__mainTime">{start.format('LLLL')}</div>
				<div className="bookingAppointment__reviewStep__appointment__subMainTime">
					{duration} {t('BookAppointment_Schedule_Duration')} {end.format('LT')}
				</div>
				<div className="bookingAppointment__reviewStep__appointment__servicesWrapper">{parts}</div>
			</div>
		);
	}

	private renderPrice(appointment: BookingListItemDto) {
		const { t, shopBookingTemplates, shopCard } = this.props;
		const currencyKey = shopCard.currencyKey;

		let originalPriceTotal = 0;

		appointment.availableBookings.forEach((ab) => {
			const service = (shopBookingTemplates.services || []).find((s) => s.id === ab.bookingTemplateId);
			originalPriceTotal = originalPriceTotal + (JSON.parse(service.customData || '{}').priceSum || ab.originalPrice);
		});

		const discountPrice = appointment.price - originalPriceTotal;

		return (
			<>
				{originalPriceTotal !== appointment.price ? (
					<>
						<div className="bookingAppointment__reviewStep__subtotalPrice">
							<span>{t('BSPortal_Common_Subtotal')}</span>
							<span>{formatCurrency(currencyKey, originalPriceTotal)}</span>
						</div>

						<div className="bookingAppointment__reviewStep__discount">
							<span>{t('Portal_TopMenu_Shop_Discounts')}</span>
							<span>{formatCurrency(currencyKey, discountPrice)}</span>
						</div>

						<div className="bookingAppointment__reviewStep__totalPrice">
							<span>{t('BSPortal_Common_Total')}</span>
							<span>{formatCurrency(currencyKey, appointment.price)}</span>
						</div>
					</>
				) : (
					<div className="bookingAppointment__reviewStep__totalPriceEqual">
						<span>{t('BSPortal_Common_Total')}</span>
						<span>{formatCurrency(currencyKey, appointment.price)}</span>
					</div>
				)}
			</>
		);
	}

	public render() {
		const { t, handleSubmit, shopCard, currentFormValues, formErrors, submitFailed, bookAppointment } = this.props;
		const appointment = currentFormValues?.selectedAppointment;
		const { selectedPayment } = this.state;

		if (!shopCard || !appointment) {
			return <Spinner />;
		}

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: bookAppointment?.error || false,
			message: bookAppointment?.message || null,
			location,
		};

		const payNowDisabled = (appointment?.price && appointment.price < 5) || !shopCard.isOnlinePaymentEnabled;
		const payAtSalonDisabled = !shopCard.allowUnpaidOrders;

		return (
			<form className="bookingAppointment__reviewStep" onSubmit={handleSubmit(this.onSubmit)}>
				<h1>{t('BookAppointment_Review_Title')}</h1>
				<div className="profileError">
					<ErrorSummary {...errorSummaryProps} />
				</div>
				{this.renderService(appointment)}
				{this.renderPrice(appointment)}
				<div className="bookingAppointment__reviewStep_subtitle">
					<h2 className="font-weight-bold">{t('BookAppointment_Review_Page_Subtitle')}</h2>
					<p>{t('BookAppointment_Review_Choose_Payment')}</p>
				</div>
				<PortalPageSection
					className={`bookingAppointment__customerStep__myselfOnly ${selectedPayment === 'payLater' ? 'active' : ''} ${
						payAtSalonDisabled ? 'disabled' : ''
					}`}
					onClick={() => !payAtSalonDisabled && this.setState({ selectedPayment: 'payLater' })}
				>
					<p className="customer-name">{t('BookAppointment_Review_Pay_At_Salon')}</p>
					<p className="customer-phone">{t('BookAppointment_Review_Pay_At_Salon_Desc')}</p>
				</PortalPageSection>
				<PortalPageSection
					className={`bookingAppointment__customerStep__myselfOnly ${selectedPayment === 'payNow' ? 'active' : ''} ${
						payNowDisabled ? 'disabled' : ''
					}`}
					onClick={() => !payNowDisabled && this.setState({ selectedPayment: 'payNow' })}
				>
					<p className="customer-name">{t('BookAppointment_Review_Pay_Now')}</p>
					<p className="customer-phone">{t('BookAppointment_Review_Pay_Now_Desc')}</p>
				</PortalPageSection>
				<div className="bookingAppointment__bottomContent">
					<div className="container">
						<div className="row">
							<div className="col-lg-9 col-xl-6 offset-lg-3">
								<div className="bookingAppointment__bottomContent__multiple">
									<Button onClick={this.props.previousPage}>
										{renderIcon('arrow_left_small')} {t('BSPortal_Common_Back')}
									</Button>
									<Button type="submit" disabled={!currentFormValues.selectedAppointment || bookAppointment.isFetching}>
										{t('BookAppointment_Review_Button')}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		);
	}
}

let BookAppointmentStepReview = reduxForm({
	form: c.FORM_NAME_BOOKAPPOINTMENT,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(BookAppointmentStepReviewForm);

const stateToProps = (state: IStore) => {
	let currentFormValues: IBookAppointmentObject = null;
	if (state.form[c.FORM_NAME_BOOKAPPOINTMENT] && state.form[c.FORM_NAME_BOOKAPPOINTMENT].values) {
		currentFormValues = state.form[c.FORM_NAME_BOOKAPPOINTMENT].values;
	}

	return {
		currentFormValues,
		shopBookingTemplates: state.shopBookingTemplates,
		shopCard: state.shopcard.shopcard,
		bookAppointment: state.bookAppointment,
		userPreferences: state.userPreferences,
		formErrors: getFormSyncErrors(c.FORM_NAME_BOOKAPPOINTMENT)(state),
		serviceGroups: state.serviceGroups.serviceGroups || [],
		userShippingAddresses: state.userShippingAddresses,
	};
};

BookAppointmentStepReview = connect(stateToProps)(BookAppointmentStepReview);

export { BookAppointmentStepReview };
